import {FC, useCallback, useEffect, useState} from 'react';

import BaseModal from '@/shared/components/modals/base/BaseModal';
import {cookieConsentService} from '@/shared/services/cookieConsent';

import CookieConsentInitial from './components/CookieConsentInitial';
import CookieConsentPreferences from './components/CookieConsentPreferences';
import {MODAL_STATE} from './consts';
import {useCookieConsent} from './hooks/useCookieConsent';
import {CookiePreferences} from './types';

const CookieConsentModal: FC = () => {
  const {
    isOpen,
    openMode,
    initialPreferences,
    onDecision,
    onClosePreferencesMode,
  } = useCookieConsent();

  const [modalState, setModalState] = useState<keyof typeof MODAL_STATE>(
    MODAL_STATE.initial
  );

  const resetState = useCallback(() => {
    setModalState(MODAL_STATE.initial);
  }, []);

  const handleClose = useCallback(() => {
    if (openMode === 'preferences') {
      onClosePreferencesMode();
      return;
    }

    resetState();
  }, [openMode, onClosePreferencesMode, resetState]);

  const acceptAll = useCallback(() => {
    onDecision(
      cookieConsentService.createConsent({
        statistics: true,
        marketing: true,
      })
    );
  }, [onDecision]);

  const rejectNonEssential = useCallback(() => {
    onDecision(
      cookieConsentService.createConsent({
        statistics: false,
        marketing: false,
      })
    );
  }, [onDecision]);

  const savePreferences = useCallback(
    (preferences: CookiePreferences) => {
      onDecision(
        cookieConsentService.createConsent({
          statistics: preferences.statistics,
          marketing: preferences.marketing,
        })
      );
    },
    [onDecision]
  );

  const openPreferencesStep = useCallback(() => {
    setModalState(MODAL_STATE.preference);
  }, []);

  const initializeModalState = useCallback(() => {
    if (openMode === 'preferences') {
      setModalState(MODAL_STATE.preference);
      return;
    }

    resetState();
  }, [openMode, resetState]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    initializeModalState();
  }, [isOpen, initializeModalState]);

  if (!isOpen) {
    return null;
  }

  return (
    <BaseModal
      onClose={handleClose}
      disappearToBottom={false}
      withCloseButton={modalState === MODAL_STATE.preference}
      className="mx-4 max-h-[98dvh] w-full max-w-[343px] sm:max-w-[664px]"
    >
      {modalState === MODAL_STATE.initial ? (
        <CookieConsentInitial
          onCustomizeSettings={openPreferencesStep}
          onAcceptAll={acceptAll}
        />
      ) : (
        <CookieConsentPreferences
          initialPreferences={initialPreferences}
          onAcceptAll={acceptAll}
          onRejectNonEssential={rejectNonEssential}
          onSavePreferences={savePreferences}
        />
      )}
    </BaseModal>
  );
};

export default CookieConsentModal;
